// Client Mercure — abonnement temps réel au hub via Server-Sent Events.
//
// Le backend (API Platform) publie automatiquement chaque création / modification /
// suppression des ressources marquées `mercure: true` sur des topics de la forme :
//   http://localhost:8000/api/salons/35
// On s'abonne avec le template URI `{id}` pour recevoir toutes les instances d'une ressource.
//
// Payloads reçus :
//  - création / modification : la ressource complète sérialisée (mêmes groupes que l'API)
//  - suppression : uniquement { "@id": "/api/salons/35" }

import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// Les topics publiés par API Platform sont des IRI absolues basées sur l'hôte de l'API
const TOPIC_BASE = API_URL.replace(/\/api\/?$/, '');

export const MERCURE_HUB_URL =
    process.env.REACT_APP_MERCURE_URL || 'http://localhost:3001/.well-known/mercure';

/** Topic template pour une ressource : mercureTopic('salons') → http://…/api/salons/{id} */
export function mercureTopic(resource) {
    return `${TOPIC_BASE}/api/${resource}/{id}`;
}

/** Extrait l'id numérique d'une IRI ("/api/salons/35" → 35) */
export function iriId(iri) {
    const m = /\/(\d+)$/.exec(iri || '');
    return m ? Number(m[1]) : null;
}

/** Extrait le nom de ressource d'une IRI ("/api/salons/35" → "salons") */
export function iriResource(iri) {
    const m = /\/api\/([^/]+)\/\d+$/.exec(iri || '');
    return m ? m[1] : null;
}

/** Une suppression est publiée avec uniquement des clés JSON-LD (@id, …) */
function isDeletePayload(payload) {
    return Object.keys(payload).every((k) => k.startsWith('@'));
}

/**
 * S'abonne au hub Mercure pour une liste de ressources API Platform.
 *
 * @param {string[]} resources  ex: ['salons', 'stands', 'projets']
 * @param {(resource: string, payload: object, meta: { deleted: boolean, id: number|null }) => void} onEvent
 * @param {boolean} [enabled=true]  permet de désactiver l'abonnement (ex: page en pause)
 *
 * La reconnexion est automatique : EventSource retente seul, et si le navigateur
 * abandonne (connexion fermée), on recrée la connexion après 5 s.
 */
export function useMercure(resources, onEvent, enabled = true) {
    const cbRef = useRef(onEvent);
    cbRef.current = onEvent;
    const key = (resources || []).join('|');

    useEffect(() => {
        if (!enabled || !key || typeof EventSource === 'undefined') return undefined;
        let es = null;
        let retryTimer = null;
        let stopped = false;

        const connect = () => {
            const url = new URL(MERCURE_HUB_URL);
            key.split('|').forEach((r) => url.searchParams.append('topic', mercureTopic(r)));
            es = new EventSource(url.toString());

            es.onmessage = (e) => {
                let payload;
                try { payload = JSON.parse(e.data); } catch { return; }
                const iri = payload['@id'];
                const resource = iriResource(iri);
                if (!resource) return;
                cbRef.current?.(resource, payload, {
                    deleted: isDeletePayload(payload),
                    id: payload.id ?? iriId(iri),
                });
            };

            es.onerror = () => {
                if (stopped) return;
                // EventSource retente seul tant que readyState = CONNECTING ;
                // s'il ferme définitivement (hub redémarré…), on recrée après 5 s.
                if (es.readyState === EventSource.CLOSED) {
                    retryTimer = setTimeout(connect, 5000);
                }
            };
        };

        connect();

        return () => {
            stopped = true;
            if (retryTimer) clearTimeout(retryTimer);
            if (es) es.close();
        };
    }, [key, enabled]);
}

/**
 * Variante simple : rappelle `refresh` (débounced) dès qu'un événement arrive
 * sur l'une des ressources. Pratique pour les pages qui rechargent leur liste
 * via une fonction existante (fetchAll, fetchData…).
 *
 * @param {string[]} resources  ex: ['clients']
 * @param {() => void} refresh  fonction de rechargement de la page
 * @param {number} [delay=1500] fenêtre de regroupement des événements (ms)
 */
export function useMercureRefresh(resources, refresh, delay = 1500) {
    const timerRef = useRef(null);
    const refreshRef = useRef(refresh);
    refreshRef.current = refresh;

    useMercure(resources, () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            refreshRef.current?.();
        }, delay);
    });

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
}
